/**
 * Ticket Intelligence Utilities
 *
 * Automatically categorizes, prioritizes, and routes tickets based on
 * user input and context. Designed to minimize user effort while
 * maximizing triage accuracy.
 */

import {
  TicketType,
  Priority,
  IncidentCategory,
  RequestCategory,
  IncidentSubcategory,
  RequestSubcategory,
} from '../types/ticket';

// ============================================
// Types
// ============================================

export interface TicketClassification {
  ticketType: TicketType;
  category: IncidentCategory | RequestCategory;
  subcategory: IncidentSubcategory | RequestSubcategory;
  priority: Priority;
  confidence: number; // 0-1, how confident we are in this classification
  suggestedTags: string[];
}

export interface ClassificationContext {
  description: string;
  userDepartment?: string;
  userRole?: string;
  timeOfDay?: 'business' | 'after-hours';
}

// ============================================
// Keyword Matching Utilities
// ============================================

/**
 * Cache for compiled regex patterns to avoid repeated compilation.
 * Key is the keyword, value is the compiled RegExp.
 */
const keywordPatternCache = new Map<string, RegExp>();

/**
 * Creates a regex pattern that matches a keyword with word boundaries.
 * Handles multi-word phrases and special characters properly.
 *
 * @param keyword - The keyword or phrase to match
 * @returns Compiled RegExp for word boundary matching
 */
function getKeywordPattern(keyword: string): RegExp {
  const cached = keywordPatternCache.get(keyword);
  if (cached) {
    return cached;
  }

  // Escape special regex characters in the keyword
  const escaped = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  // Use word boundaries for matching
  // \b works for word characters, but we need special handling for apostrophes
  const pattern = new RegExp(`\\b${escaped}\\b`, 'i');

  keywordPatternCache.set(keyword, pattern);
  return pattern;
}

/**
 * Checks if a keyword exists in the text as a complete word/phrase.
 * Prevents partial matches like "word" matching inside "password".
 *
 * @param text - The text to search in (should be lowercase)
 * @param keyword - The keyword to find (case-insensitive matching)
 * @returns True if the keyword is found as a complete word/phrase
 */
function matchesKeyword(text: string, keyword: string): boolean {
  return getKeywordPattern(keyword).test(text);
}

/**
 * Finds all matching keywords from a list in the given text.
 *
 * @param text - The text to search in
 * @param keywords - Array of keywords to look for
 * @returns Array of keywords that were found
 */
function findMatchingKeywords(text: string, keywords: readonly string[]): string[] {
  const lower = text.toLowerCase();
  return keywords.filter((keyword) => matchesKeyword(lower, keyword));
}

// ============================================
// Keyword Patterns
// ============================================

const URGENCY_KEYWORDS = {
  critical: [
    'urgent',
    'emergency',
    'critical',
    'down',
    'outage',
    'not working at all',
    'completely broken',
    'entire team',
    'everyone',
    'all users',
    'production',
    'cannot work',
    "can't work",
    'blocked',
    'deadline',
    'asap',
    'immediately',
    'server down',
    'system down',
    'security breach',
    'data loss',
    'no one can',
    'nobody can',
    'company-wide',
    'site-wide',
    'entire office',
    'entire department',
    'ransomware',
    'hacked',
    'compromised',
    'breach',
    'all down',
    'complete outage',
  ],
  high: [
    'important',
    'soon',
    'affecting',
    'multiple',
    'several people',
    'broken',
    'failing',
    'error',
    'cannot access',
    "can't access",
    'locked out',
    'not responding',
    'keeps happening',
    'recurring',
    'multiple times',
    'intermittent',
    'affects team',
    'spreading',
    'getting worse',
    'degraded',
    'frustrated',
    'time-sensitive',
    'client waiting',
  ],
  low: [
    'when you get a chance',
    'no rush',
    'low priority',
    'minor',
    'small issue',
    'would be nice',
    'eventually',
    'whenever',
  ],
};

const INCIDENT_PATTERNS: Record<IncidentCategory, { keywords: string[]; subcategoryMap: Record<string, IncidentSubcategory> }> = {
  [IncidentCategory.HARDWARE]: {
    keywords: [
      'laptop',
      'computer',
      'monitor',
      'screen',
      'keyboard',
      'mouse',
      'printer',
      'phone',
      'headset',
      'dock',
      'docking station',
      'charger',
      'battery',
      'webcam',
      'camera',
      'not charging',
      "won't charge",
      'dead battery',
      "won't turn on",
      "won't power on",
      "won't boot",
      'no power',
      'black screen',
      'blank screen',
      'blue screen',
      'bsod',
      'cracked',
      'broken screen',
      'damaged',
      'usb',
      'external drive',
      'hard drive',
      'ssd',
      'fan noise',
      'overheating',
      'touchpad',
      'trackpad',
      'speakers',
      'audio',
      'sound',
      'microphone',
      'mic',
      'display',
      'hdmi',
      'displayport',
      'adapter',
      'cable',
      'dongle',
      'peripheral',
    ],
    subcategoryMap: {
      laptop: IncidentSubcategory.LAPTOP,
      computer: IncidentSubcategory.DESKTOP,
      desktop: IncidentSubcategory.DESKTOP,
      monitor: IncidentSubcategory.MONITOR,
      screen: IncidentSubcategory.MONITOR,
      keyboard: IncidentSubcategory.KEYBOARD_MOUSE,
      mouse: IncidentSubcategory.KEYBOARD_MOUSE,
      printer: IncidentSubcategory.PRINTER,
      print: IncidentSubcategory.PRINTER,
      phone: IncidentSubcategory.PHONE,
      headset: IncidentSubcategory.HEADSET,
      dock: IncidentSubcategory.DOCKING_STATION,
      docking: IncidentSubcategory.DOCKING_STATION,
      'blue screen': IncidentSubcategory.LAPTOP,
      bsod: IncidentSubcategory.LAPTOP,
      "won't boot": IncidentSubcategory.LAPTOP,
      "won't turn on": IncidentSubcategory.LAPTOP,
      audio: IncidentSubcategory.HEADSET,
      sound: IncidentSubcategory.HEADSET,
      microphone: IncidentSubcategory.HEADSET,
      mic: IncidentSubcategory.HEADSET,
      speakers: IncidentSubcategory.HEADSET,
      touchpad: IncidentSubcategory.LAPTOP,
      trackpad: IncidentSubcategory.LAPTOP,
      usb: IncidentSubcategory.DOCKING_STATION,
      display: IncidentSubcategory.MONITOR,
      hdmi: IncidentSubcategory.MONITOR,
      displayport: IncidentSubcategory.MONITOR,
      webcam: IncidentSubcategory.HEADSET,
      camera: IncidentSubcategory.HEADSET,
    },
  },
  [IncidentCategory.SOFTWARE]: {
    keywords: [
      'office',
      'word',
      'excel',
      'powerpoint',
      'outlook',
      'teams',
      'onedrive',
      'sharepoint',
      'application',
      'app',
      'software',
      'program',
      'crash',
      'freezing',
      'slow',
      'error message',
      'browser',
      'chrome',
      'edge',
      "won't open",
      "doesn't open",
      "can't open",
      "won't launch",
      "doesn't launch",
      'keeps crashing',
      'keeps freezing',
      'unresponsive',
      'not responding',
      'hung',
      'hanging',
      'stuck',
      'loading forever',
      'stuck loading',
      'update failed',
      "won't update",
      "won't install",
      'missing',
      'disappeared',
      'virus',
      'malware',
      'popup',
      'pop-up',
      'license expired',
      'activation',
      'corrupted',
      'repair',
      'reinstall',
      'zoom',
      'slack',
      'adobe',
      'pdf',
      'antivirus',
      'defender',
      'microsoft',
      'windows',
      'mac',
      'macos',
    ],
    subcategoryMap: {
      office: IncidentSubcategory.OFFICE_365,
      word: IncidentSubcategory.OFFICE_365,
      excel: IncidentSubcategory.OFFICE_365,
      powerpoint: IncidentSubcategory.OFFICE_365,
      teams: IncidentSubcategory.TEAMS,
      onedrive: IncidentSubcategory.ONEDRIVE,
      sharepoint: IncidentSubcategory.SHAREPOINT,
      browser: IncidentSubcategory.BROWSER,
      chrome: IncidentSubcategory.BROWSER,
      edge: IncidentSubcategory.BROWSER,
      zoom: IncidentSubcategory.OTHER_SOFTWARE,
      slack: IncidentSubcategory.OTHER_SOFTWARE,
      adobe: IncidentSubcategory.OTHER_SOFTWARE,
      pdf: IncidentSubcategory.OTHER_SOFTWARE,
      antivirus: IncidentSubcategory.OTHER_SOFTWARE,
      defender: IncidentSubcategory.OTHER_SOFTWARE,
      virus: IncidentSubcategory.OTHER_SOFTWARE,
      malware: IncidentSubcategory.OTHER_SOFTWARE,
      windows: IncidentSubcategory.OTHER_SOFTWARE,
      mac: IncidentSubcategory.OTHER_SOFTWARE,
      macos: IncidentSubcategory.OTHER_SOFTWARE,
    },
  },
  [IncidentCategory.NETWORK]: {
    keywords: [
      'vpn',
      'wifi',
      'internet',
      'network',
      'connection',
      'connected',
      'ethernet',
      'lan',
      'slow connection',
      'network drive',
      'shared drive',
      'mapped drive',
      'disconnected',
      'keeps disconnecting',
      'drops',
      'dropping',
      'intermittent',
      'no internet',
      "can't connect",
      'unable to connect',
      'connection failed',
      'connection timeout',
      'lag',
      'laggy',
      'latency',
      'slow internet',
      'bandwidth',
      'router',
      'modem',
      'firewall',
      'proxy',
      'dns',
      'ip address',
      'dhcp',
      'wireless',
      'wired',
      'hotspot',
      'remote desktop',
      'rdp',
      'citrix',
      'remote access',
    ],
    subcategoryMap: {
      vpn: IncidentSubcategory.VPN,
      wifi: IncidentSubcategory.WIFI,
      wireless: IncidentSubcategory.WIFI,
      ethernet: IncidentSubcategory.ETHERNET,
      lan: IncidentSubcategory.ETHERNET,
      internet: IncidentSubcategory.INTERNET,
      'network drive': IncidentSubcategory.NETWORK_DRIVE,
      'shared drive': IncidentSubcategory.NETWORK_DRIVE,
      'mapped drive': IncidentSubcategory.NETWORK_DRIVE,
      disconnected: IncidentSubcategory.INTERNET,
      'no internet': IncidentSubcategory.INTERNET,
      'remote desktop': IncidentSubcategory.VPN,
      rdp: IncidentSubcategory.VPN,
      citrix: IncidentSubcategory.VPN,
      firewall: IncidentSubcategory.INTERNET,
      proxy: IncidentSubcategory.INTERNET,
      dns: IncidentSubcategory.INTERNET,
      hotspot: IncidentSubcategory.WIFI,
    },
  },
  [IncidentCategory.ACCESS]: {
    keywords: [
      // Password-related (user-friendly language)
      'password',
      'my password',
      'forgot password',
      'forgot my password',
      'reset password',
      'reset my password',
      'change password',
      'change my password',
      'password not working',
      'password expired',
      'wrong password',
      "don't know my password",
      "don't remember my password",
      "can't remember my password",
      'need to reset',
      'need my password',
      'password issue',
      'password problem',

      // Login-related (user-friendly language)
      'login',
      'log in',
      'logging in',
      'logged in',
      "can't login",
      "can't log in",
      'cannot login',
      'cannot log in',
      "won't let me login",
      "won't let me log in",
      "won't let me in",
      'tried to login',
      'tried to log in',
      'trying to login',
      'trying to log in',
      'unable to login',
      'unable to log in',
      'login failed',
      'login error',
      'login issue',
      'login problem',
      "can't get in",
      "can't get into",

      // Sign in variations
      'sign in',
      'signin',
      'signing in',
      'signed in',
      "can't sign in",
      'cannot sign in',

      // Account-related
      'locked',
      'locked out',
      'account locked',
      'account disabled',
      'account suspended',
      'account',
      'my account',
      'disabled',
      'suspended',
      'deactivated',
      'unlock',
      'unlock my account',

      // Authentication
      'mfa',
      'authenticator',
      '2fa',
      'two factor',
      'verification code',
      'security code',
      'code not working',
      "didn't get the code",
      "didn't receive code",
      'authentication',
      'authentication failed',
      'credentials',
      'session expired',
      'session timeout',
      'timed out',
      'expired',

      // Access/Permissions
      'permission',
      'access denied',
      'unauthorized',
      'not authorized',
      "don't have access",
      "can't access",
      'no access',

      // SSO
      'sso',
      'single sign on',
      'okta',
      'azure ad',
    ],
    subcategoryMap: {
      password: IncidentSubcategory.PASSWORD_RESET,
      'my password': IncidentSubcategory.PASSWORD_RESET,
      'forgot password': IncidentSubcategory.PASSWORD_RESET,
      'forgot my password': IncidentSubcategory.PASSWORD_RESET,
      'reset password': IncidentSubcategory.PASSWORD_RESET,
      'reset my password': IncidentSubcategory.PASSWORD_RESET,
      'change password': IncidentSubcategory.PASSWORD_RESET,
      'change my password': IncidentSubcategory.PASSWORD_RESET,
      'password not working': IncidentSubcategory.PASSWORD_RESET,
      'wrong password': IncidentSubcategory.PASSWORD_RESET,
      'need to reset': IncidentSubcategory.PASSWORD_RESET,
      credentials: IncidentSubcategory.PASSWORD_RESET,
      locked: IncidentSubcategory.ACCOUNT_LOCKED,
      'locked out': IncidentSubcategory.ACCOUNT_LOCKED,
      'account locked': IncidentSubcategory.ACCOUNT_LOCKED,
      'account disabled': IncidentSubcategory.ACCOUNT_LOCKED,
      login: IncidentSubcategory.ACCOUNT_LOCKED,
      'log in': IncidentSubcategory.ACCOUNT_LOCKED,
      'sign in': IncidentSubcategory.ACCOUNT_LOCKED,
      signin: IncidentSubcategory.ACCOUNT_LOCKED,
      'login failed': IncidentSubcategory.ACCOUNT_LOCKED,
      'login error': IncidentSubcategory.ACCOUNT_LOCKED,
      "can't login": IncidentSubcategory.ACCOUNT_LOCKED,
      "can't log in": IncidentSubcategory.ACCOUNT_LOCKED,
      'cannot login': IncidentSubcategory.ACCOUNT_LOCKED,
      'session expired': IncidentSubcategory.ACCOUNT_LOCKED,
      'disabled account': IncidentSubcategory.ACCOUNT_LOCKED,
      disabled: IncidentSubcategory.ACCOUNT_LOCKED,
      suspended: IncidentSubcategory.ACCOUNT_LOCKED,
      deactivated: IncidentSubcategory.ACCOUNT_LOCKED,
      mfa: IncidentSubcategory.MFA_ISSUE,
      authenticator: IncidentSubcategory.MFA_ISSUE,
      '2fa': IncidentSubcategory.MFA_ISSUE,
      'two factor': IncidentSubcategory.MFA_ISSUE,
      sso: IncidentSubcategory.MFA_ISSUE,
      okta: IncidentSubcategory.MFA_ISSUE,
      'single sign on': IncidentSubcategory.MFA_ISSUE,
      'verification code': IncidentSubcategory.MFA_ISSUE,
      'security code': IncidentSubcategory.MFA_ISSUE,
      'code not working': IncidentSubcategory.MFA_ISSUE,
      permission: IncidentSubcategory.PERMISSION_ISSUE,
      'access denied': IncidentSubcategory.PERMISSION_ISSUE,
      unauthorized: IncidentSubcategory.PERMISSION_ISSUE,
      'not authorized': IncidentSubcategory.PERMISSION_ISSUE,
      "don't have access": IncidentSubcategory.PERMISSION_ISSUE,
      'no access': IncidentSubcategory.PERMISSION_ISSUE,
    },
  },
  [IncidentCategory.EMAIL]: {
    keywords: [
      'email',
      'outlook',
      'calendar',
      'meeting',
      'invite',
      'mailbox',
      'distribution list',
      'dl',
      'sync',
      'not receiving',
      'sending',
      "can't send",
      'cannot send',
      "won't send",
      "can't receive",
      'cannot receive',
      'stuck in outbox',
      'bounced',
      'bounce back',
      'undeliverable',
      'attachment',
      'attachments',
      'signature',
      'out of office',
      'ooo',
      'spam filter',
      'junk',
      'blocked sender',
      'rules',
      'forwarding',
      'delegate',
      'shared mailbox',
      'archive',
      'pst',
      'ost',
      'autodiscover',
      'exchange',
    ],
    subcategoryMap: {
      outlook: IncidentSubcategory.OUTLOOK,
      calendar: IncidentSubcategory.CALENDAR,
      meeting: IncidentSubcategory.CALENDAR,
      invite: IncidentSubcategory.CALENDAR,
      'distribution list': IncidentSubcategory.DISTRIBUTION_LIST,
      dl: IncidentSubcategory.DISTRIBUTION_LIST,
      mailbox: IncidentSubcategory.MAILBOX_FULL,
      sync: IncidentSubcategory.EMAIL_SYNC,
      "can't send": IncidentSubcategory.OUTLOOK,
      'cannot send': IncidentSubcategory.OUTLOOK,
      "can't receive": IncidentSubcategory.OUTLOOK,
      'cannot receive': IncidentSubcategory.OUTLOOK,
      'stuck in outbox': IncidentSubcategory.OUTLOOK,
      bounced: IncidentSubcategory.OUTLOOK,
      attachment: IncidentSubcategory.OUTLOOK,
      'shared mailbox': IncidentSubcategory.MAILBOX_FULL,
      junk: IncidentSubcategory.OUTLOOK,
      spam: IncidentSubcategory.OUTLOOK,
      archive: IncidentSubcategory.MAILBOX_FULL,
      pst: IncidentSubcategory.OUTLOOK,
      ost: IncidentSubcategory.OUTLOOK,
    },
  },
  [IncidentCategory.OTHER]: {
    keywords: [],
    subcategoryMap: {},
  },
};

const REQUEST_PATTERNS: Record<RequestCategory, { keywords: string[]; subcategoryMap: Record<string, RequestSubcategory> }> = {
  [RequestCategory.NEW_HARDWARE]: {
    keywords: [
      'need laptop',
      'need computer',
      'need monitor',
      'new laptop',
      'new computer',
      'new monitor',
      'order',
      'purchase',
      'upgrade',
      'replacement',
      'need keyboard',
      'need mouse',
      'need headset',
      'need phone',
      'need dock',
      'new keyboard',
      'new mouse',
      'new headset',
      'new phone',
      'new dock',
      'broken laptop',
      'broken monitor',
      'damaged equipment',
      'request laptop',
      'request computer',
      'request monitor',
    ],
    subcategoryMap: {
      laptop: RequestSubcategory.NEW_LAPTOP,
      computer: RequestSubcategory.NEW_DESKTOP,
      desktop: RequestSubcategory.NEW_DESKTOP,
      monitor: RequestSubcategory.NEW_MONITOR,
      peripheral: RequestSubcategory.NEW_PERIPHERAL,
      phone: RequestSubcategory.NEW_PHONE,
      upgrade: RequestSubcategory.HARDWARE_UPGRADE,
      keyboard: RequestSubcategory.NEW_PERIPHERAL,
      mouse: RequestSubcategory.NEW_PERIPHERAL,
      headset: RequestSubcategory.NEW_PERIPHERAL,
      dock: RequestSubcategory.NEW_PERIPHERAL,
    },
  },
  [RequestCategory.SOFTWARE_INSTALL]: {
    keywords: [
      'install',
      'software',
      'application',
      'program',
      'license',
      'download',
      'need software',
      'need app',
      'need application',
      'need program',
      'install zoom',
      'install slack',
      'install adobe',
      'install chrome',
      'install teams',
      'install office',
      'need license',
      'request license',
      'need zoom',
      'need slack',
      'need adobe',
    ],
    subcategoryMap: {
      standard: RequestSubcategory.STANDARD_SOFTWARE,
      license: RequestSubcategory.LICENSED_SOFTWARE,
      developer: RequestSubcategory.DEVELOPER_TOOLS,
      zoom: RequestSubcategory.STANDARD_SOFTWARE,
      slack: RequestSubcategory.STANDARD_SOFTWARE,
      adobe: RequestSubcategory.LICENSED_SOFTWARE,
      chrome: RequestSubcategory.STANDARD_SOFTWARE,
    },
  },
  [RequestCategory.ACCESS_REQUEST]: {
    keywords: [
      'access to',
      'permission to',
      'need access',
      'grant access',
      'shared drive access',
      'folder access',
      'application access',
      'join group',
      'add to group',
      'distribution list',
      'mailing list',
      'team access',
      'project access',
      'system access',
      'database access',
      'add permission',
      'request permission',
      'add me to',
      'join team',
    ],
    subcategoryMap: {
      'shared drive': RequestSubcategory.SHARED_DRIVE,
      folder: RequestSubcategory.SHARED_DRIVE,
      application: RequestSubcategory.APPLICATION_ACCESS,
      vpn: RequestSubcategory.VPN_ACCESS,
      admin: RequestSubcategory.ADMIN_ACCESS,
      'distribution list': RequestSubcategory.DISTRIBUTION_LIST_ADD,
      'mailing list': RequestSubcategory.DISTRIBUTION_LIST_ADD,
      group: RequestSubcategory.DISTRIBUTION_LIST_ADD,
      team: RequestSubcategory.APPLICATION_ACCESS,
      database: RequestSubcategory.APPLICATION_ACCESS,
    },
  },
  [RequestCategory.ACCOUNT_SETUP]: {
    keywords: [
      'new hire',
      'new employee',
      'contractor',
      'onboarding',
      'new user',
      'create account',
      'service account',
      'new starter',
      'new joiner',
      'temp worker',
      'temporary worker',
      'intern',
      'consultant',
    ],
    subcategoryMap: {
      'new hire': RequestSubcategory.NEW_HIRE,
      'new employee': RequestSubcategory.NEW_HIRE,
      onboarding: RequestSubcategory.NEW_HIRE,
      'new starter': RequestSubcategory.NEW_HIRE,
      'new joiner': RequestSubcategory.NEW_HIRE,
      contractor: RequestSubcategory.CONTRACTOR,
      consultant: RequestSubcategory.CONTRACTOR,
      'temp worker': RequestSubcategory.CONTRACTOR,
      intern: RequestSubcategory.CONTRACTOR,
      'service account': RequestSubcategory.SERVICE_ACCOUNT,
    },
  },
  [RequestCategory.EQUIPMENT_RETURN]: {
    keywords: [
      'return',
      'termination',
      'leaving',
      'offboarding',
      'turn in',
      'hand in',
      'last day',
      'resign',
      'retiring',
      'end of contract',
      'leaving company',
    ],
    subcategoryMap: {
      laptop: RequestSubcategory.LAPTOP_RETURN,
      equipment: RequestSubcategory.FULL_EQUIPMENT_RETURN,
      termination: RequestSubcategory.TERMINATION,
      offboarding: RequestSubcategory.TERMINATION,
      resign: RequestSubcategory.TERMINATION,
      retiring: RequestSubcategory.TERMINATION,
      'last day': RequestSubcategory.TERMINATION,
    },
  },
  [RequestCategory.OTHER]: {
    keywords: [],
    subcategoryMap: {},
  },
};

const REQUEST_INDICATORS = [
  'need',
  'want',
  'request',
  'please install',
  'can i get',
  'can i have',
  'would like',
  'looking for',
  'require',
  'new',
  'order',
  'purchase',
  'set up',
  'setup',
  'create',
  'add me to',
  'grant',
  'access to',
];

// ============================================
// Classification Functions
// ============================================

/**
 * Determines if the description indicates a request (vs incident)
 */
function isRequest(description: string): boolean {
  const requestMatches = findMatchingKeywords(description, REQUEST_INDICATORS);

  // Check for incident indicators (problems, issues)
  const incidentIndicators = [
    'not working',
    "doesn't work",
    "won't",
    "can't",
    'broken',
    'error',
    'failed',
    'issue',
    'problem',
    'help',
    'stuck',
    'frozen',
    'slow',
    'crash',
    'stopped',
    'failing',
    'unable',
    'crashing',
    'hanging',
    'hung',
    'disconnecting',
    'timing out',
    'timed out',
    'no longer',
    'not anymore',
    'keep getting',
    'keeps showing',
    'keeps asking',
    'keeps crashing',
    'keeps freezing',
    'unresponsive',
    'not responding',
  ];
  const incidentMatches = findMatchingKeywords(description, incidentIndicators);

  return requestMatches.length > incidentMatches.length;
}

/**
 * Detects priority based on urgency keywords and context
 */
function detectPriority(description: string, context: ClassificationContext): Priority {
  // Check for critical keywords
  if (findMatchingKeywords(description, URGENCY_KEYWORDS.critical).length > 0) {
    return Priority.P2; // Users rarely have true P1s - that's for major outages
  }

  // Check for high urgency keywords
  if (findMatchingKeywords(description, URGENCY_KEYWORDS.high).length > 0) {
    return Priority.P3;
  }

  // Check for low priority indicators
  if (findMatchingKeywords(description, URGENCY_KEYWORDS.low).length > 0) {
    return Priority.P4;
  }

  // Default priority based on context
  if (context.timeOfDay === 'after-hours') {
    return Priority.P4; // After-hours requests are typically less urgent
  }

  return Priority.P3; // Default to medium priority
}

/**
 * Detects incident category and subcategory
 */
function detectIncidentCategory(description: string): {
  category: IncidentCategory;
  subcategory: IncidentSubcategory;
  confidence: number;
} {
  let bestMatch: {
    category: IncidentCategory;
    subcategory: IncidentSubcategory;
    confidence: number;
  } = {
    category: IncidentCategory.OTHER,
    subcategory: IncidentSubcategory.GENERAL,
    confidence: 0,
  };

  for (const [category, patterns] of Object.entries(INCIDENT_PATTERNS)) {
    const matchedKeywords = findMatchingKeywords(description, patterns.keywords);
    const confidence = matchedKeywords.length / Math.max(patterns.keywords.length, 1);

    if (matchedKeywords.length > 0 && confidence >= bestMatch.confidence) {
      // Find best subcategory match - check longer phrases first for better accuracy
      let subcategory: IncidentSubcategory = IncidentSubcategory.GENERAL;
      const subcategoryEntries = Object.entries(patterns.subcategoryMap)
        .sort((a, b) => b[0].length - a[0].length);

      for (const [subKw, sub] of subcategoryEntries) {
        if (matchesKeyword(description.toLowerCase(), subKw)) {
          subcategory = sub;
          break;
        }
      }

      bestMatch = {
        category: category as IncidentCategory,
        subcategory,
        confidence: Math.min(confidence + 0.3, 1), // Boost confidence if we found keywords
      };
    }
  }

  return bestMatch;
}

/**
 * Detects request category and subcategory
 */
function detectRequestCategory(description: string): {
  category: RequestCategory;
  subcategory: RequestSubcategory;
  confidence: number;
} {
  let bestMatch: {
    category: RequestCategory;
    subcategory: RequestSubcategory;
    confidence: number;
  } = {
    category: RequestCategory.OTHER,
    subcategory: RequestSubcategory.GENERAL_REQUEST,
    confidence: 0,
  };

  for (const [category, patterns] of Object.entries(REQUEST_PATTERNS)) {
    const matchedKeywords = findMatchingKeywords(description, patterns.keywords);
    const confidence = matchedKeywords.length / Math.max(patterns.keywords.length, 1);

    if (matchedKeywords.length > 0 && confidence >= bestMatch.confidence) {
      // Find best subcategory match - check longer phrases first for better accuracy
      let subcategory: RequestSubcategory = RequestSubcategory.GENERAL_REQUEST;
      const subcategoryEntries = Object.entries(patterns.subcategoryMap)
        .sort((a, b) => b[0].length - a[0].length);

      for (const [subKw, sub] of subcategoryEntries) {
        if (matchesKeyword(description.toLowerCase(), subKw)) {
          subcategory = sub;
          break;
        }
      }

      bestMatch = {
        category: category as RequestCategory,
        subcategory,
        confidence: Math.min(confidence + 0.3, 1),
      };
    }
  }

  return bestMatch;
}

/**
 * Extracts suggested tags from description
 */
function extractTags(description: string): string[] {
  const tags: Set<string> = new Set();

  // Application tags - use word boundaries to avoid false matches
  const apps = [
    'teams',
    'outlook',
    'excel',
    'word',
    'sharepoint',
    'onedrive',
    'vpn',
    'chrome',
    'edge',
    'zoom',
    'slack',
    'adobe',
    'pdf',
    'antivirus',
    'citrix',
    'rdp',
  ];
  apps.forEach((app) => {
    if (matchesKeyword(description, app)) tags.add(app);
  });

  // Hardware tags
  const hardware = [
    'laptop',
    'monitor',
    'keyboard',
    'mouse',
    'printer',
    'phone',
    'headset',
    'dock',
    'webcam',
    'camera',
    'usb',
    'microphone',
  ];
  hardware.forEach((hw) => {
    if (matchesKeyword(description, hw)) tags.add(hw);
  });

  // Issue type tags based on patterns
  if (findMatchingKeywords(description, ['slow', 'lag', 'latency', 'laggy']).length > 0) {
    tags.add('performance');
  }
  if (findMatchingKeywords(description, ['crash', 'frozen', 'freezing', 'hung', 'crashing']).length > 0) {
    tags.add('stability');
  }
  if (findMatchingKeywords(description, ['error', 'failed', 'failing']).length > 0) {
    tags.add('error');
  }
  if (findMatchingKeywords(description, ['access', 'permission', 'denied']).length > 0) {
    tags.add('access');
  }
  if (findMatchingKeywords(description, ['login', 'log in', 'sign in', 'password', 'credentials']).length > 0) {
    tags.add('authentication');
  }
  if (findMatchingKeywords(description, ['network', 'wifi', 'internet', 'connection', 'connected']).length > 0) {
    tags.add('connectivity');
  }
  if (findMatchingKeywords(description, ['update', 'install', 'reinstall']).length > 0) {
    tags.add('installation');
  }
  if (findMatchingKeywords(description, ['missing', 'disappeared', 'gone']).length > 0) {
    tags.add('missing');
  }
  if (findMatchingKeywords(description, ['email', 'mailbox', 'calendar']).length > 0) {
    tags.add('email');
  }
  if (findMatchingKeywords(description, ['virus', 'malware', 'security', 'hacked', 'breach']).length > 0) {
    tags.add('security');
  }

  return Array.from(tags).slice(0, 5); // Max 5 tags
}

/**
 * Main classification function - analyzes description and context
 * to automatically categorize and prioritize a ticket
 */
export function classifyTicket(context: ClassificationContext): TicketClassification {
  const { description } = context;

  // Determine ticket type
  const ticketType = isRequest(description) ? TicketType.REQUEST : TicketType.INCIDENT;

  // Get category and subcategory based on type
  let category: IncidentCategory | RequestCategory;
  let subcategory: IncidentSubcategory | RequestSubcategory;
  let confidence: number;

  if (ticketType === TicketType.INCIDENT) {
    const detection = detectIncidentCategory(description);
    category = detection.category;
    subcategory = detection.subcategory;
    confidence = detection.confidence;
  } else {
    const detection = detectRequestCategory(description);
    category = detection.category;
    subcategory = detection.subcategory;
    confidence = detection.confidence;
  }

  // Determine priority
  const priority = detectPriority(description, context);

  // Extract tags
  const suggestedTags = extractTags(description);

  return {
    ticketType,
    category,
    subcategory,
    priority,
    confidence,
    suggestedTags,
  };
}

/**
 * Gets a human-readable priority label
 */
export function getPriorityLabel(priority: Priority): string {
  switch (priority) {
    case Priority.P1:
      return 'Critical - Immediate response';
    case Priority.P2:
      return 'High - Response within 1 hour';
    case Priority.P3:
      return 'Medium - Response within 4 hours';
    case Priority.P4:
      return 'Low - Response within 24 hours';
    default:
      return 'Standard';
  }
}

/**
 * Gets expected response time based on priority
 */
export function getExpectedResponseTime(priority: Priority): string {
  switch (priority) {
    case Priority.P1:
      return '15 minutes';
    case Priority.P2:
      return '1 hour';
    case Priority.P3:
      return '4 hours';
    case Priority.P4:
      return '24 hours';
    default:
      return '4 hours';
  }
}

/**
 * Gets current time context
 */
export function getTimeContext(): 'business' | 'after-hours' {
  const now = new Date();
  const hour = now.getHours();
  const day = now.getDay();

  // Weekend
  if (day === 0 || day === 6) {
    return 'after-hours';
  }

  // Before 8am or after 6pm
  if (hour < 8 || hour >= 18) {
    return 'after-hours';
  }

  return 'business';
}
